// AssetListModal - 挙動別カテゴリに分けたアセット一覧モーダル
// じめん / みず / かざり / たからもの / ボタン に分類し、各グループに説明を付ける
// トップ画面と同じ専用ダイアログ（InScreenModal + BuilderModalPanel）で表示する。

import { useDispatch, useSelector } from "react-redux";
import { useRef, useMemo, useEffect, useState } from "react";
import { produce } from "immer";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { AssetModel } from "common/model";
import { usePlaySound } from "common/utils";
import assets from "features/builder/assets";
import { actions } from "features/builder/slice";
import { HELP_ASSET } from "features/builder/helpPages";
import { InScreenModal, BuilderModalPanel, BuilderModalAccent } from "features/builder/elements";
import { BuilderHelpPopup } from "./BuilderHelpPopup";

// アセット枠の基準サイズ（筐体スケールを継承するため固定 px）
const ITEM_BASE = 100;

// カテゴリヘッダー
const CategoryHeader = ({
  icon,
  label,
  hint,
  place,
  color,
}: {
  icon: string;
  label: string;
  hint?: string;
  place?: string;
  color: string;
}) => (
  <div className="flex-col-el items-start w-full gap-1">
    <div className={`flex-row-el items-center gap-2 ${color}`}>
      <span className="material-symbols-outlined type-md">{icon}</span>
      <span className="type-text type-body">{label}</span>
    </div>
    {hint && (
      <div className="flex-row-el items-center gap-1 text-textcolor-black">
        <span className="material-symbols-outlined type-sm">info</span>
        <span className="type-text-left type-sm">{hint}</span>
      </div>
    )}
    {/* どこに置けるかは、置いてみるまで分からないので先に書いておく */}
    {place && (
      <div className="flex-row-el items-center gap-1 text-textcolor-black">
        <span className="material-symbols-outlined type-sm">add_circle</span>
        <span className="type-text-left type-sm">{place}</span>
      </div>
    )}
  </div>
);

// アセットグリッドアイテム
const AssetGridItem = ({
  asset,
  selected,
  onSelect,
}: {
  asset: AssetModel;
  selected: boolean;
  onSelect: (asset: AssetModel) => void;
}) => (
  <div
    onClick={() => onSelect(asset)}
    style={{ width: ITEM_BASE, height: ITEM_BASE }}
    className={`flex-col-el flex-center shrink-0 rounded-xl overflow-hidden cursor-pointer transition-all duration-150 active:scale-95 border-solid border-2
      ${
        selected
          ? "border-blue-100 bg-blue-10 shadow-md"
          : "border-transparent bg-beige-60 hover:bg-white hover:shadow-sm"
      }`}
  >
    <img
      draggable="false"
      className="w-full h-full object-contain select-none p-2"
      src={`${Constants.assetHost}/assets/models/${asset.name}.png`}
    />
  </div>
);

export const AssetListModal = () => {
  const play = usePlaySound();
  const dispatch = useDispatch();
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedSlotIndex = useSelector((state: RootState) => state.builder.selectedSlotIndex);
  const show = useSelector((state: RootState) => state.builder.actionMenu.assetList);
  const slot = useSelector((state: RootState) => state.builder.slot);
  const [showHelp, setShowHelp] = useState(false);

  // 挙動別カテゴリ分類（各グループに説明を付ける）
  const categories = useMemo(
    () => [
      {
        key: "ground",
        models: assets.find((a) => a.name === "ground")?.models ?? [],
        icon: "landscape",
        color: "text-orange-100",
        bgColor: "bg-orange-100/10",
        label: I18n.t("MSG_BUILDER_ASSET_CATEGORY_GROUND"),
        hint: I18n.t("MSG_BUILDER_ASSET_CATEGORY_GROUND_HINT"),
        place: I18n.t("MSG_BUILDER_ASSET_PLACE_ON_TILE"),
      },
      {
        key: "water",
        models: assets.find((a) => a.name === "river")?.models ?? [],
        icon: "water",
        color: "text-blue-100",
        bgColor: "bg-blue-100/10",
        label: I18n.t("MSG_BUILDER_ASSET_CATEGORY_WATER"),
        hint: I18n.t("MSG_BUILDER_ASSET_CATEGORY_WATER_HINT"),
        place: I18n.t("MSG_BUILDER_ASSET_PLACE_ON_TILE"),
      },
      {
        key: "decoration",
        models: assets.find((a) => a.name === "decoration")?.models ?? [],
        icon: "forest",
        color: "text-green-100",
        bgColor: "bg-green-100/10",
        label: I18n.t("MSG_BUILDER_ASSET_CATEGORY_DECORATION"),
        hint: I18n.t("MSG_BUILDER_ASSET_CATEGORY_DECORATION_HINT"),
        place: I18n.t("MSG_BUILDER_ASSET_PLACE_ON_GROUND"),
      },
      {
        key: "gem",
        models: assets.find((a) => a.name === "gem")?.models ?? [],
        icon: "diamond",
        color: "text-purple",
        bgColor: "bg-purple/10",
        label: I18n.t("MSG_BUILDER_ASSET_CATEGORY_GEM"),
        hint: I18n.t("MSG_BUILDER_ASSET_CATEGORY_GEM_HINT"),
        place: I18n.t("MSG_BUILDER_ASSET_PLACE_ON_GROUND"),
      },
      {
        key: "button",
        models: assets.find((a) => a.name === "condition")?.models ?? [],
        icon: "touch_app",
        color: "text-red-100",
        bgColor: "bg-red-100/10",
        label: I18n.t("MSG_BUILDER_ASSET_CATEGORY_BUTTON"),
        hint: I18n.t("MSG_BUILDER_ASSET_CATEGORY_BUTTON_HINT"),
        place: I18n.t("MSG_BUILDER_ASSET_PLACE_ON_GROUND"),
      },
    ],
    [],
  );

  // スロット選択初期化
  useEffect(() => {
    if (show && (selectedSlotIndex === null || selectedSlotIndex === undefined)) {
      dispatch(actions.selectSlot(0));
    }
  }, [show, selectedSlotIndex, dispatch]);

  const handleClose = () => {
    play();
    const slotAsset = slot[selectedSlotIndex ?? 0];
    if (!slotAsset) {
      dispatch(actions.selectSlot(null));
      dispatch(actions.updateActionMenu({ key: "tap", value: true, exclusive: true }));
    }
    dispatch(actions.updateActionMenu({ key: "assetList", value: false }));
  };

  const handleSelectAsset = (asset: AssetModel) => {
    play();
    const existingIndex = slot.findIndex((v) => v?.name === asset.name);
    const targetIndex = selectedSlotIndex ?? 0;

    if (existingIndex === -1) {
      // 新規追加
      const newSlot = produce(slot, (draft) => {
        draft[targetIndex] = asset;
      });
      dispatch(actions.updateSlot(newSlot));
    } else {
      // 既存を移動
      const newSlot = produce(slot, (draft) => {
        draft[existingIndex] = null;
        draft[targetIndex] = asset;
      });
      dispatch(actions.updateSlot(newSlot));
    }
    dispatch(actions.selectedAssetMesh(asset));
  };

  const isSelected = (asset: AssetModel) => slot.some((v) => v?.name === asset.name);

  return (
    <InScreenModal open={show} onBackdropClose={handleClose}>
      <BuilderModalPanel
        accent={BuilderModalAccent.BROWN}
        icon="category"
        title={I18n.t("MSG_BUILDER_ASSET_LIST_TITLE")}
        width={880}
        onClose={handleClose}
        onHelp={() => setShowHelp(true)}
      >
        {/* コンテンツ */}
        <div
          ref={scrollRef}
          data-guide="asset_list"
          className="flex-col-el items-start w-full gap-6 max-h-[440px] overflow-y-auto invisible-scrollbar"
        >
          {categories
            .filter((category) => category.models.length > 0)
            .map((category) => (
              <div
                key={category.key}
                data-guide="asset_category"
                className={`flex-col-el items-start w-full gap-2 p-4 rounded-2xl box-border ${category.bgColor}`}
              >
                <CategoryHeader
                  icon={category.icon}
                  label={category.label}
                  hint={category.hint}
                  place={category.place}
                  color={category.color}
                />
                <div data-guide="asset_grid" className="flex-row-el flex-wrap w-full gap-3">
                  {category.models.map((asset, index) => (
                    <AssetGridItem
                      key={`${category.key}_${index}`}
                      asset={asset}
                      selected={isSelected(asset)}
                      onSelect={handleSelectAsset}
                    />
                  ))}
                </div>
              </div>
            ))}
        </div>
      </BuilderModalPanel>

      <BuilderHelpPopup open={showHelp} onClose={() => setShowHelp(false)} chapters={HELP_ASSET} />
    </InScreenModal>
  );
};
